import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { AlertCircle, Calendar, SearchLg, XClose } from "@untitledui/icons";
import { Button } from "@tricura/atlas-ui";
import { Skeleton } from "../../../components/skeleton";
import { SubmissionFooter } from "../../../components/submission-footer";
import { useFacilities } from "../../../hooks/use-facilities";
import { useLitigationData } from "../../../hooks/use-litigation";
import type { LitigationFacilityRow, MapMarker } from "../../../types/litigation";
import { useSubmissionContext } from "../context";
import { getNextSection } from "../sections";
import { EMPTY_FILTERS, HeatmapFilters, type HeatmapFilterState } from "./heatmap-filters";
import { HeatmapTable } from "./heatmap-table";
import { Map } from "./map/map";

const SECTION_ID = "legal";

const RISK_LABEL: Record<string, string> = {
    low: "Low Risk",
    "almost-high": "Almost High",
    medium: "Medium Risk",
    high: "High Risk",
    "weak-match": "Weak Match",
};


function uniqueStrings<T extends keyof LitigationFacilityRow>(rows: LitigationFacilityRow[], key: T): string[] {
    const seen = new Set<string>();
    for (const r of rows) {
        const value = r[key];
        if (typeof value === "string" && value.length > 0) seen.add(value);
    }
    return Array.from(seen).sort();
}

function applyFilters(rows: LitigationFacilityRow[], filters: HeatmapFilterState): LitigationFacilityRow[] {
    return rows.filter((r) => {
        if (filters.type.size > 0 && !filters.type.has(r.type)) return false;
        if (filters.facilityName.size > 0 && !filters.facilityName.has(r.facilityName)) return false;
        if (filters.risk.size > 0 && !filters.risk.has(RISK_LABEL[r.riskLevel] ?? r.riskLevel)) return false;
        if (filters.county.size > 0 && !filters.county.has(r.county)) return false;
        if (filters.state.size > 0 && !filters.state.has(r.state)) return false;
        return true;
    });
}

function rowToMarker(row: LitigationFacilityRow): MapMarker {
    return {
        lat: row.lat,
        lng: row.lng,
        riskLevel: row.riskLevel,
        label: row.facilityName,
        type: row.type,
        city: row.city,
        county: row.county,
        state: row.state,
        licensedBeds: row.licensedBeds,
        lawsuitsPer100Beds: row.lawsuitsPer100Beds,
    };
}

export function HeatmapPage() {
    const { detail } = useSubmissionContext();
    const navigate = useNavigate();
    const [year, setYear] = useState<number>(5);
    const [filters, setFilters] = useState<HeatmapFilterState>(EMPTY_FILTERS);

    const next = getNextSection(SECTION_ID);

    // Read-only section — no Save button (the footer omits it when there's
    // no onSave). Continue just navigates to the next section.
    const onContinue = () => {
        navigate(next ? `/submissions/${detail.id}/${next.path}` : "/submissions");
    };

    const query = useLitigationData(detail.id, year);
    // Archive state lives on the facility SoT, not on the litigation payload.
    const { state: facilitiesState, update: updateFacility, save: saveFacilities } = useFacilities(detail.id);
    const [restoreError, setRestoreError] = useState<string | null>(null);
    /** Chains restore clicks so their `saveFacilities()` calls never overlap
     *  — `useFacilities`'s `save()` diffs the whole shared working copy
     *  against one baseline and isn't safe to run concurrently with itself,
     *  and clicking Restore on several archived rows in a row is the one
     *  place in the app that could otherwise trigger that. */
    const restoreQueueRef = useRef<Promise<void>>(Promise.resolve());
    /**
     * Restores a facility from this page's ⋮. Unlike Schedule/Regulatory/
     * Operational/Loss Runs, Legal has no Save button or dirty-state UI to
     * flush a staged change later (it's a read-only data-viz page — see the
     * footer below), so this commits immediately: stage, then flush right
     * away rather than leaving an invisible unsaved edit behind. If the save
     * fails, the optimistic local change is rolled back (re-excluding the
     * facility) and an error banner tells the user to retry — otherwise the
     * row would look restored here while the server still has it archived,
     * with no Save button to ever notice or fix that.
     */
    const restoreFacility = useCallback(
        (locationId: string) => {
            restoreQueueRef.current = restoreQueueRef.current.then(async () => {
                setRestoreError(null);
                await updateFacility(locationId, { isExcluded: false });
                const ok = await saveFacilities();
                if (!ok) {
                    await updateFacility(locationId, { isExcluded: true });
                    setRestoreError("Couldn't restore the facility. Please try again.");
                }
            });
        },
        [updateFacility, saveFacilities],
    );
    // Memoize the conditional `rows` so dependent useMemo hooks below
    // get a stable reference between renders.
    const rows = useMemo(() => (query.status === "ready" ? query.data : []), [query]);

    const options = useMemo(
        () => ({
            type: uniqueStrings(rows, "type"),
            facilityName: uniqueStrings(rows, "facilityName"),
            risk: Array.from(new Set(rows.map((r) => RISK_LABEL[r.riskLevel] ?? r.riskLevel))).sort(),
            county: uniqueStrings(rows, "county"),
            state: uniqueStrings(rows, "state"),
        }),
        [rows],
    );

    const filtered = useMemo(() => applyFilters(rows, filters), [rows, filters]);
    /**
     * Archived facilities (OR-1431). Legal is the one section that HIDES them
     * rather than only striking them through: per the design they'd add noise to
     * the claims map, since they're no longer part of the decision. They stay in
     * the table as historical data, struck through, with a tooltip saying the
     * marker is gone until they're restored.
     *
     * The join is by location id — matching on facility name would be
     * ambiguous, and duplicate names on one submission are a real case
     * (OR-1409 / OR-1417), so a name join could hide the wrong marker.
     */
    const archivedLocationIds = useMemo(() => {
        const ids = new Set<string>();
        if (facilitiesState.status !== "ready") return ids;
        for (const f of facilitiesState.data) if (f.isExcluded) ids.add(f.id);
        return ids;
    }, [facilitiesState]);
    const isRowArchived = useCallback(
        (row: LitigationFacilityRow) => archivedLocationIds.has(row.locationId),
        [archivedLocationIds],
    );
    const markers = useMemo(
        () => filtered.filter((r) => !isRowArchived(r)).map(rowToMarker),
        [filtered, isRowArchived],
    );

    const isLoading = query.status === "loading";
    const isError = query.status === "error";

    const renewal = detail.accountBasics.renewalDate || "";
    const expiration = detail.accountBasics.expirationDate || "";

    return (
        // `flex-1` mirrors the Regulatory page: makes this root fill
        // the layout `<section>`'s flex column so the SubmissionFooter
        // (with its `mt-auto`) lands pinned to the viewport bottom
        // even when the page content is shorter than the viewport.
        <div className="flex flex-1 flex-col gap-6">
            {/* Page header — title + subtitle on the left, the policy
              * date pair on the right (matches the strip on UW
              * Information and Schedule). */}
            <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="flex flex-col gap-1">
                    <h2 className="text-primary text-md font-semibold">Legal</h2>
                    <p className="text-tertiary text-sm">Geographic risk view</p>
                </div>
                <div className="flex w-full items-stretch justify-between gap-6 md:w-auto md:shrink-0 md:justify-start">
                    <DateField label="Renewal Date" value={renewal} />
                    <span aria-hidden="true" className="bg-border-secondary hidden w-px self-stretch md:block" />
                    <DateField label="Expiration Date" value={expiration} />
                </div>
            </header>

            {isError && (
                <p className="text-error-primary border-secondary bg-primary rounded-lg border px-6 py-10 text-center text-sm">
                    Couldn't load litigation data: {query.error.message}
                </p>
            )}

            {/* Restore-facility failure (dismissible) — Legal has no Save
              * button to retry from otherwise, see `restoreFacility` above. */}
            {restoreError && (
                <div
                    role="alert"
                    className="border-error-secondary bg-error-primary text-error-primary flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm"
                >
                    <span className="inline-flex items-center gap-2">
                        <AlertCircle aria-hidden="true" className="size-4" />
                        {restoreError}
                    </span>
                    <button type="button" aria-label="Dismiss" onClick={() => setRestoreError(null)}>
                        <XClose aria-hidden="true" className="size-4" />
                    </button>
                </div>
            )}

            {!isError && (
                // Single rounded container hosting tabs + map + filters
                // + table. Replaces the prior two-card layout so the
                // Legal section reads as one cohesive panel.
                <div className="border-secondary bg-primary flex flex-col gap-4 overflow-hidden rounded-lg border p-4">
                    {/* Card header — "Claims Heat map" title on the left, the
                      * Filters button on the right (latest Figma). Filters are
                      * grouped under one button that opens the side drawer; they
                      * apply across both the map and the table. */}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <h3 className="text-primary text-base font-semibold">Claims Heat map</h3>
                        <HeatmapFilters
                            filters={filters}
                            onChange={setFilters}
                            options={options}
                            year={year}
                            onYearChange={setYear}
                            resultCount={filtered.length}
                            data-autotest="legal-heatmap-filters-button"
                        />
                    </div>

                    <div className="flex flex-col gap-4">
                        {isLoading ? <Skeleton className="h-[480px] w-full" /> : <Map markers={markers} showLegend={false} />}

                        {isLoading ? (
                            <div className="flex flex-col gap-3">
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <Skeleton key={i} className="h-8 w-full" />
                                ))}
                            </div>
                        ) : filtered.length === 0 ? (
                            // No facilities match the active filters. The map
                            // stays above; the table area shows this empty
                            // state with a quick "Clear filters" reset.
                            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
                                <div className="border-secondary mb-2 inline-flex size-11 items-center justify-center rounded-lg border">
                                    <SearchLg aria-hidden="true" className="text-fg-quaternary size-5" />
                                </div>
                                <h3 className="text-primary text-sm font-semibold">No results match these filters</h3>
                                <p className="text-tertiary text-sm">Try adjusting your filters or clearing them all.</p>
                                <Button color="secondary" size="sm" className="mt-2" onClick={() => setFilters(EMPTY_FILTERS)}>
                                    Clear filters
                                </Button>
                            </div>
                        ) : (
                            // Full-bleed table + pagination — `-mx-4 -mb-4`
                            // cancels the panel's `p-4` so the table reaches
                            // the card border.
                            <div className="-mx-4 -mb-4">
                                <HeatmapTable
                                    isRowArchived={isRowArchived}
                                    onRestoreFacility={restoreFacility}
                                    rows={filtered}
                                    className="rounded-none shadow-none ring-0"
                                    paginationClassName="px-4"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Legal is a read-only data-viz page — no Save CTA, just a
              * navigation Continue/Finish (Andressa Q&A, jun-18). */}
            <SubmissionFooter onContinue={onContinue} nextSectionLabel={next?.label ?? null} />
        </div>
    );
}

/** Same date-strip pattern used on UW Information + Schedule headers.
 *  Inlined here so the heat-map page stays self-contained (each page
 *  carries its own tiny presentational helpers). */
function DateField({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className="text-tertiary text-xs font-normal">{label}</span>
            <span className="text-secondary inline-flex items-center gap-1.5 text-xs font-semibold">
                <Calendar aria-hidden="true" className="text-fg-quaternary size-4" />
                {value || <span className="text-placeholder font-normal">—</span>}
            </span>
        </div>
    );
}
